import React, { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Product as ApiProduct } from '../services/api';

type Product = ApiProduct;

export interface QuickAddSelection {
  variantId?: number;
  sizeName?: string;
  colorName?: string;
  colorCode?: string;
  quantity: number;
  unitPrice?: number;
  compareAtPrice?: number;
  inStock?: boolean;
}

interface QuickAddToCartModalProps {
  isOpen: boolean;
  product: Product | null;
  onClose: () => void;
  onConfirm: (selection: QuickAddSelection) => void;
}

const QuickAddToCartModal: React.FC<QuickAddToCartModalProps> = ({ isOpen, product, onClose, onConfirm }) => {
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [showColorWarning, setShowColorWarning] = useState(false);
  const [showSizeWarning, setShowSizeWarning] = useState(false);

  // If modal is opened while unauthenticated, redirect to login immediately
  useEffect(() => {
    if (isOpen) {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !product) return;
    // reset selections - let user choose
    setQuantity(1);
    setSelectedColor('');
    setSelectedSize('');
    setShowColorWarning(false);
    setShowSizeWarning(false);
  }, [isOpen, product]);

  // Remove auto-selection - let user choose size manually

  const availableColors = useMemo(() => {
    if (!product?.variants) return [] as { color: string; color_code?: string }[];
    const map = new Map<string, { color: string; color_code?: string }>();
    for (const v of product.variants) {
      if (!map.has(v.color)) map.set(v.color, { color: v.color, color_code: (v as any).color_code });
    }
    return Array.from(map.values());
  }, [product?.variants]);

  const colorVariant = useMemo(() => {
    if (!product?.variants) return undefined;
    return product.variants.find(v => v.color === selectedColor && v.is_active) || product.variants.find(v => v.color === selectedColor);
  }, [product?.variants, selectedColor]);

  const availableSizes = useMemo(() => {
    if (!colorVariant) return [] as { name: string; stock: number; is_active?: boolean }[];
    return (colorVariant.sizes || []).map(s => {
      // Calculate available stock (total stock minus reserved stock)
      const totalStock = s.stock_quantity || 0;
      const reservedStock = s.reserved_quantity || 0;
      const availableStock = Math.max(0, totalStock - reservedStock);
      return { name: s.name, stock: availableStock, is_active: s.is_active };
    });
  }, [colorVariant]);

  const currentSizeData = useMemo(() => {
    if (!colorVariant) return undefined;
    return colorVariant.sizes?.find(s => s.name === selectedSize);
  }, [colorVariant, selectedSize]);

  // Calculate available stock (total stock minus reserved stock)
  const totalStock = currentSizeData?.stock_quantity || 0;
  const reservedStock = currentSizeData?.reserved_quantity || 0;
  const stockQuantity = Math.max(0, totalStock - reservedStock);
  const isInStock = stockQuantity > 0;

  const basePrice = currentSizeData?.price || colorVariant?.price || product?.price || 0;
  const comparePrice = currentSizeData?.compare_at_price || colorVariant?.compare_at_price || product?.compare_at_price;

  // Use backend-computed promotional_price first, then fall back to computing from active_promotions
  const getPromoPrice = (base: number): number | undefined => {
    // 1. Use saved promotional_price from backend if it's less than base
    const saved = product?.promotional_price;
    if (saved && saved > 0 && saved < base) return saved;

    // 2. Fall back to computing from active_promotions
    const promo = (product as any)?.active_promotions?.[0];
    if (!promo || base <= 0) return undefined;
    const type = promo.type;
    const value = promo.value;
    if (type === 'percentage') {
      const rawDiscount = base * (value / 100);
      const maxCap = promo.max_discount_amount || promo.max_discount || null;
      const applied = maxCap ? Math.min(rawDiscount, maxCap) : rawDiscount;
      return Math.max(0, Number((base - applied).toFixed(2)));
    }
    if (type === 'fixed' || type === 'fixed_amount') {
      return Math.max(0, Number((base - value).toFixed(2)));
    }
    return undefined;
  };

  const promoPrice = getPromoPrice(basePrice);
  const unitPrice = (promoPrice ?? basePrice) || 0;
  const compareAtForCart = promoPrice !== undefined ? basePrice : (comparePrice && comparePrice > basePrice ? comparePrice : undefined);

  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Select options</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          {/* Colors */}
          {availableColors.length > 0 && (
            <div>
              <div className="text-sm font-medium text-gray-900 mb-2">Color</div>
              <div className="flex items-center gap-2 flex-wrap">
                {availableColors.map(c => (
                  <button
                    key={c.color}
                    onClick={() => { 
                      setSelectedColor(c.color); 
                      setSelectedSize(''); // Reset size when color changes - let user choose
                      setShowColorWarning(false); // Hide warning when color is selected
                    }}
                    className={`w-8 h-8 rounded-full border-2 transition ${selectedColor === c.color ? 'border-gray-900 scale-110' : 'border-gray-300 hover:border-gray-400'}`}
                    style={{ backgroundColor: c.color_code }}
                    title={c.color}
                  />
                ))}
                {selectedColor && <span className="text-xs text-gray-600 ml-1">{selectedColor}</span>}
              </div>
              {showColorWarning && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Please select a color
                </p>
              )}
            </div>
          )}

          {/* Sizes */}
          {availableSizes.length > 0 && (
            <div>
              <div className="text-sm font-medium text-gray-900 mb-2">Size</div>
              <div className="flex items-center gap-2 flex-wrap">
                {availableSizes.map(s => {
                  const disabled = !s.is_active || s.stock <= 0;
                  return (
                    <button
                      key={s.name}
                      onClick={() => {
                        if (!disabled) {
                          setSelectedSize(s.name);
                          setShowSizeWarning(false); // Hide warning when size is selected
                        }
                      }}
                      disabled={disabled}
                      className={`px-3 py-1.5 text-sm border rounded-md ${selectedSize === s.name ? 'border-gray-900 bg-gray-900 text-white' : disabled ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed' : 'border-gray-300 hover:border-gray-400'}`}
                    >
                      {s.name}
                    </button>
                  );
                })}
              </div>
              {showSizeWarning && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Please select a size
                </p>
              )}
            </div>
          )}

          {/* Quantity */}
          <div>
            <div className="text-sm font-medium text-gray-900 mb-2">Quantity</div>
            <div className="inline-flex items-center border border-gray-300 rounded-md">
              <button
                onClick={() => setQuantity(q => Math.max(1, q - 1))}
                className="px-3 py-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-l-md"
              >
                −
              </button>
              <span className="px-4 py-1.5 border-x border-gray-300 bg-gray-50 font-medium text-sm">{quantity}</span>
              <button
                onClick={() => setQuantity(q => (stockQuantity ? Math.min(stockQuantity, q + 1) : q + 1))}
                className="px-3 py-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-r-md"
                disabled={stockQuantity ? quantity >= stockQuantity : false}
              >
                +
              </button>
            </div>
            {stockQuantity > 0 && (
              <div className="text-xs text-gray-500 mt-1">In stock: {stockQuantity}</div>
            )}
          </div>

          {/* Price */}
          <div className="flex items-center gap-3">
            <span className="text-lg font-bold text-gray-900">{unitPrice.toLocaleString("en-US")} MMK</span>
            {compareAtForCart && (
              <span className="text-sm text-gray-500 line-through">{Number(compareAtForCart).toLocaleString("en-US")} MMK</span>
            )}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50">Cancel</button>
          <button
            onClick={() => {
              // Reset warnings
              setShowColorWarning(false);
              setShowSizeWarning(false);
              
              // Check if product has variants and user hasn't selected options
              if (product?.variants?.length) {
                let hasError = false;
                
                if (!selectedColor) {
                  setShowColorWarning(true);
                  hasError = true;
                }
                if (!selectedSize) {
                  setShowSizeWarning(true);
                  hasError = true;
                }
                if (!isInStock && selectedSize) {
                  // Don't proceed if out of stock
                  return;
                }
                
                if (hasError) {
                  return;
                }
              }
              
              onConfirm({
                variantId: colorVariant?.id as any,
                sizeName: selectedSize || undefined,
                colorName: selectedColor || (colorVariant as any)?.color,
                colorCode: (colorVariant as any)?.color_code,
                quantity,
                unitPrice,
                compareAtPrice: compareAtForCart,
                inStock: isInStock,
              });
            }}
            className="px-4 py-2 text-sm rounded-md font-semibold bg-gray-900 text-white hover:bg-gray-800"
          >
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickAddToCartModal;
